/**
 * Mobile Menu Functionality
 *
 * Handles opening/closing the mobile menu with WCAG compliance:
 * - Proper ARIA attributes
 * - Keyboard navigation (ESC to close, focus trap)
 * - Focus management
 * - Body scroll lock when menu is open
 */

( function() {
	'use strict';

	// Wait for DOM to be ready
	function initMobileMenu() {
		const mobileMenuToggle = document.getElementById( 'mobile-menu-toggle' );
		const mobileMenu = document.getElementById( 'mobile-menu' );
		const mobileMenuClose = document.getElementById( 'mobile-menu-close' );

		// Return early if elements don't exist
		if ( ! mobileMenuToggle || ! mobileMenu || ! mobileMenuClose ) {
			return;
		}

		const body = document.body;

		// Get all focusable elements within the mobile menu
		const getFocusableElements = () => {
			const focusableSelectors = [
				'a[href]',
				'button:not([disabled])',
				'textarea:not([disabled])',
				'input:not([disabled])',
				'select:not([disabled])',
				'[tabindex]:not([tabindex="-1"])',
			];
			return mobileMenu.querySelectorAll( focusableSelectors.join( ', ' ) );
		};

		// Get first and last focusable elements
		const getFirstFocusable = () => {
			const focusable = getFocusableElements();
			return focusable.length > 0 ? focusable[0] : null;
		};

		const getLastFocusable = () => {
			const focusable = getFocusableElements();
			return focusable.length > 0 ? focusable[focusable.length - 1] : null;
		};

		// Store the element that had focus before opening menu
		let previousActiveElement = null;

		// Open mobile menu
		const openMobileMenu = () => {
			mobileMenu.classList.remove( 'translate-x-full' );
			mobileMenu.setAttribute( 'aria-hidden', 'false' );
			mobileMenuToggle.setAttribute( 'aria-expanded', 'true' );
			
			// Store the element that had focus
			previousActiveElement = document.activeElement;
			
			// Prevent body scroll
			body.style.overflow = 'hidden';
			
			// Focus the close button
			setTimeout( () => {
				mobileMenuClose.focus();
			}, 100 );
		};

		// Close mobile menu
		const closeMobileMenu = () => {
			mobileMenu.classList.add( 'translate-x-full' );
			mobileMenu.setAttribute( 'aria-hidden', 'true' );
			mobileMenuToggle.setAttribute( 'aria-expanded', 'false' );
			
			// Restore body scroll
			body.style.overflow = '';
			
			// Return focus to the toggle button
			if ( previousActiveElement ) {
				previousActiveElement.focus();
				previousActiveElement = null;
			}
		};

		// Handle ESC key to close menu
		const handleEscapeKey = ( event ) => {
			if ( event.key === 'Escape' && mobileMenu.getAttribute( 'aria-hidden' ) === 'false' ) {
				closeMobileMenu();
			}
		};

		// Handle focus trap - when tabbing on last element, go to first
		const handleFocusTrap = ( event ) => {
			if ( mobileMenu.getAttribute( 'aria-hidden' ) === 'false' ) {
				const firstFocusable = getFirstFocusable();
				const lastFocusable = getLastFocusable();
				
				if ( firstFocusable && lastFocusable ) {
					if ( event.shiftKey && document.activeElement === firstFocusable ) {
						// Shift + Tab on first element - go to last
						event.preventDefault();
						lastFocusable.focus();
					} else if ( ! event.shiftKey && document.activeElement === lastFocusable ) {
						// Tab on last element - go to first
						event.preventDefault();
						firstFocusable.focus();
					}
				}
			}
		};

		// Toggle menu on button click
		mobileMenuToggle.addEventListener( 'click', function( event ) {
			event.preventDefault();
			event.stopPropagation();
			
			const isHidden = mobileMenu.getAttribute( 'aria-hidden' ) === 'true';
			if ( isHidden ) {
				openMobileMenu();
			} else {
				closeMobileMenu();
			}
		} );

		// Close menu on close button click
		mobileMenuClose.addEventListener( 'click', function( event ) {
			event.preventDefault();
			event.stopPropagation();
			closeMobileMenu();
		} );

		// Close menu when clicking outside (on overlay/backdrop)
		mobileMenu.addEventListener( 'click', function( event ) {
			// If clicking directly on the menu container (not on a child), close it
			if ( event.target === mobileMenu ) {
				closeMobileMenu();
			}
		} );

		// Handle ESC key
		document.addEventListener( 'keydown', handleEscapeKey );

		// Handle focus trap
		document.addEventListener( 'keydown', function( event ) {
			if ( event.key === 'Tab' ) {
				handleFocusTrap( event );
			}
		} );

		// Close menu when a link is clicked (for single-page navigation)
		const mobileMenuLinks = mobileMenu.querySelectorAll( 'a' );
		mobileMenuLinks.forEach( function( link ) {
			link.addEventListener( 'click', function() {
				// Only close if it's not an external link or anchor
				const href = link.getAttribute( 'href' );
				if ( href && ! href.startsWith( '#' ) && ! href.startsWith( 'http' ) ) {
					setTimeout( function() {
						closeMobileMenu();
					}, 100 );
				}
			} );
		} );

		// Handle window resize - close menu if switching to desktop view
		let resizeTimer;
		window.addEventListener( 'resize', function() {
			clearTimeout( resizeTimer );
			resizeTimer = setTimeout( function() {
				if ( window.innerWidth >= 1024 && mobileMenu.getAttribute( 'aria-hidden' ) === 'false' ) {
					closeMobileMenu();
				}
			}, 250 );
		} );
	}

	// Initialize when DOM is ready
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', initMobileMenu );
	} else {
		// DOM is already ready
		initMobileMenu();
	}
}() );
